import type {
  CellRendererSelectorResult,
  ColDef,
  DefaultMenuItem,
  GridOptions,
  ICellRendererParams,
  InitialGroupOrderComparatorParams,
  IRowNode,
  KeyCreatorParams,
  MenuItemDef,
  RowSelectionOptions,
  ValueGetterParams,
} from "ag-grid-community";

import type { RawIconName } from "../../../icons/iconMarkup";
import { plainRowGroupKey } from "../../rowGroupState";
import type { GridRowGroups } from "../../types";
import AgGridLoadingCell from "./AgGridLoadingCell.vue";
import AgGridRowGroupLabel from "./AgGridRowGroupLabel.vue";
import { compareRowGroupKeys, EMPTY_ROW_GROUP_KEY, groupKeyComparator, rowGroupColumnComparator } from "./rowGroupSort";
import { cellValue, isGroupRow, serverRowGroupOf } from "./serverRowGroupData";

/** Styled in rowGroupPanel.scss. */
const ROW_GROUP_CELL_CLASS = "sca-row-group-cell";

export const ROW_GROUP_ICONS = {
  rowGroupPanel: "generic/row-groups",
  columnMoveGroup: "generic/row-groups",
  // The drag handle and the remove button of a row-group chip.
  columnDrag: "phosphor/dots-six",
  cancel: "phosphor/x",
  menuAddRowGroup: "generic/row-groups",
  // "Discard all groups" / "Ungroup by" in the Group column's menu, as in the panel's menu.
  menuRemoveRowGroup: "phosphor/broom",
  groupContracted: "phosphor/caret-right",
  groupExpanded: "phosphor/caret-down",
} as const satisfies Record<string, RawIconName>;

type RendererParamsFn = (params: {
  value: unknown;
  data: unknown;
  node: IRowNode | null;
}) => Record<string, unknown> | undefined;

/**
 * The value a row is grouped by. Many tables give a column no value of its own (its `field` is just
 * the column id) and hand the real one to the renderer as `value`, or as `label` to a status
 * renderer; that one is used then.
 */
function groupingValue({ value, data, node, colDef }: KeyCreatorParams): unknown {
  if (value != null || typeof colDef.cellRendererParams !== "function") return value;
  const passed = (colDef.cellRendererParams as RendererParamsFn)({ value, data, node });
  return passed?.value ?? passed?.label;
}

type GroupKey = NonNullable<GridRowGroups["groupKey"]>;
const keyCreators = new WeakMap<GroupKey, (params: KeyCreatorParams) => string>();

/** One `keyCreator` per `groupKey`: AG Grid regroups from scratch, collapsing every group, when it changes. */
function keyCreatorFor(groupKey: GroupKey = plainRowGroupKey): (params: KeyCreatorParams) => string {
  let keyCreator = keyCreators.get(groupKey);
  if (!keyCreator) {
    keyCreator = (params) => groupKey(groupingValue(params)) ?? EMPTY_ROW_GROUP_KEY;
    keyCreators.set(groupKey, keyCreator);
  }
  return keyCreator;
}

const colIdOf = (col: ColDef) => col.colId ?? col.field;

/** The grouping columns that still exist; a saved filterset can name a column deleted since. */
function groupedColumnIds(colDefs: ColDef[], rowGroups: GridRowGroups | undefined): string[] {
  if (!rowGroups?.enabled) return [];
  return rowGroups.columns.filter((id) => colDefs.some((col) => colIdOf(col) === id));
}

function isGroupingBy(rowGroups: GridRowGroups | undefined): rowGroups is GridRowGroups {
  return !!rowGroups?.enabled && rowGroups.columns.length > 0;
}

/** The column the Group column stands in for: the caller's `leafColumn` while visible and not grouped by. */
function rowGroupLeafColumn(colDefs: ColDef[], rowGroups: GridRowGroups | undefined): ColDef | undefined {
  const colId = rowGroups?.leafColumn;
  return colDefs.find((col) => !!colId && !col.hide && colIdOf(col) === colId && !rowGroups?.columns.includes(colId));
}

/**
 * Marks the grouping columns and hides them and the column the Group column stands in for; a group
 * row's other cells stay empty. Unchanged when the table does not group.
 */
export function applyRowGroupsToColDefs(colDefs: ColDef[], rowGroups: GridRowGroups | undefined): ColDef[] {
  if (!rowGroups) return colDefs;

  const columns = groupedColumnIds(colDefs, rowGroups);
  const leafColumn = columns.length > 0 ? rowGroupLeafColumn(colDefs, rowGroups) : undefined;

  return colDefs.map((col) => {
    const groupIndex = columns.indexOf(colIdOf(col) ?? "");
    const isGroupedBy = groupIndex >= 0;
    const existingSelector = col.cellRendererSelector;
    return {
      ...col,
      // A caller's `false` keeps a column it cannot group by out of "Group by". While grouping is off
      // the column menu shows "Group by" disabled; with the panel hidden a column has nowhere to drop.
      enableRowGroup: col.enableRowGroup !== false,
      rowGroup: isGroupedBy,
      rowGroupIndex: isGroupedBy ? groupIndex : null,
      keyCreator: rowGroups.enabled ? keyCreatorFor(rowGroups.groupKey) : col.keyCreator,
      comparator: isGroupedBy ? groupKeyComparator(col) : col.comparator,
      hide: !!col.hide || isGroupedBy || col === leafColumn,
      // The Group column carries this column's sort while it stands in for it.
      ...(col === leafColumn && { sort: null, sortIndex: null }),
      cellRendererSelector: rowGroups.enabled
        ? (params) =>
            // The Group column renders a server-side group's value with its sample row as `data`.
            isGroupRow(params.node) && (!params.data || serverRowGroupOf(params.data))
              ? { component: AgGridRowGroupLabel }
              : existingSelector?.(params)
        : existingSelector,
    };
  });
}

/** undefined leaves the cell to AG Grid's own rendering of the value. */
function rendererFor(colDef: ColDef, params: ICellRendererParams): CellRendererSelectorResult | undefined {
  const selected = colDef.cellRendererSelector?.(params);
  if (selected) return selected;
  if (!colDef.cellRenderer) return undefined;
  const rendererParams = colDef.cellRendererParams as RendererParamsFn | Record<string, unknown> | undefined;
  return {
    component: colDef.cellRenderer,
    params: typeof rendererParams === "function" ? rendererParams(params) : rendererParams,
  };
}

/**
 * The Group column filters the top grouping column, under that column's id: a nested level's
 * column may be of another kind.
 */
function topColumnFilter(col: ColDef, colId: string): ColDef {
  return {
    filter: col.filter,
    // AG Grid turns the filter off on a Group column without a field of its own (grouped by its leaf column).
    filterValueGetter: (params: ValueGetterParams) => params.getValue(colId),
    filterParams: col.filterParams,
    floatingFilter: col.floatingFilter,
    suppressFloatingFilterButton: col.suppressFloatingFilterButton,
    suppressHeaderFilterButton: col.suppressHeaderFilterButton,
    floatingFilterComponentParams: { ...col.floatingFilterComponentParams, originalHiddenColId: colId },
  };
}

/** The Group column while grouping by a column; undefined otherwise. `colDefs` are the caller's, before grouping hid any. */
export function rowGroupAutoColumnDef(
  colDefs: ColDef[],
  rowGroups: GridRowGroups | undefined,
  labels: { header: string; empty: string },
): ColDef | undefined {
  const [topColumnId] = groupedColumnIds(colDefs, rowGroups);
  if (topColumnId === undefined) return undefined;
  const leafColumn = rowGroupLeafColumn(colDefs, rowGroups);
  const topColumn = colDefs.find((col) => colIdOf(col) === topColumnId);
  const leafColumnId = leafColumn && colIdOf(leafColumn);

  return {
    headerName: labels.header,
    // Lets the column-state sync tell which column the Group column stands in for.
    field: leafColumnId,
    width: leafColumn?.width,
    pinned: leafColumn?.pinned,
    autoHeight: leafColumn?.autoHeight,
    wrapText: leafColumn?.wrapText,
    // The field above is the leaf's id, not its data path (`CATEGORY_NAME` reads `name.default`).
    valueGetter: leafColumnId ? (params: ValueGetterParams) => params.getValue(leafColumnId) : undefined,
    valueFormatter: leafColumn?.valueFormatter,
    // The total row's settings for the column the Group column stands in for.
    context: leafColumn?.context,
    sortable: !!leafColumn && leafColumn.sortable !== false,
    sort: leafColumn?.sort,
    sortIndex: leafColumn?.sortIndex,
    comparator: leafColumn && rowGroupColumnComparator(leafColumn),
    ...(topColumn && topColumnFilter(topColumn, topColumnId)),
    // AG Grid drops its group renderer (arrow, count) once the table adds a cellRendererSelector.
    cellRenderer: "agGroupCellRenderer",
    loadingCellRenderer: AgGridLoadingCell,
    cellClass: ROW_GROUP_CELL_CLASS,
    cellRendererParams: {
      suppressDoubleClickExpand: true,
      innerRendererSelector: (params: ICellRendererParams): CellRendererSelectorResult | undefined => {
        if (!params.node.group) return leafColumn ? rendererFor(leafColumn, params) : undefined;

        if (params.node.key === EMPTY_ROW_GROUP_KEY) {
          return { component: AgGridRowGroupLabel, params: { text: labels.empty } };
        }

        const groupColumn = params.node.rowGroupColumn;
        const serverGroup = serverRowGroupOf(params.node.data);
        if (serverGroup) {
          if (!groupColumn) return undefined;
          const colDef = groupColumn.getColDef();
          const data = serverGroup.row;
          const value = cellValue(colDef, data, params);
          // AG Grid hands the inner renderer the group row's own data and key unless the selector overrides them.
          const selected = rendererFor(colDef, { ...params, data, value });
          return selected && { ...selected, params: { data, value, ...selected.params } };
        }

        // A group row has no data of its own; every row in it shares the grouped value.
        const firstRow = params.node.allLeafChildren?.[0];
        if (!groupColumn || !firstRow?.data) return undefined;
        const data = firstRow.data;
        const value = params.api.getCellValue({ rowNode: firstRow, colKey: groupColumn });
        const selected = rendererFor(groupColumn.getColDef(), { ...params, node: firstRow, data, value });
        return selected && { ...selected, params: { data, value, ...selected.params } };
      },
    },
  };
}

export function isSameOrder<T>(a: readonly T[], b: readonly T[], same: (x: T, y: T) => boolean = Object.is): boolean {
  return a.length === b.length && a.every((item, index) => same(item, b[index]));
}

export function isRowGroupPanelShown(rowGroups: GridRowGroups | undefined): boolean {
  return !!rowGroups?.enabled && !rowGroups.panelHidden;
}

export function rowGroupGridOptions(rowGroups: GridRowGroups | undefined): GridOptions {
  if (!rowGroups) return {};
  return {
    rowGroupPanelShow: isRowGroupPanelShown(rowGroups) ? "always" : "never",
    // The caller owns column visibility (saved per filterset): grouping hides a column by its colDef.
    suppressGroupChangesColumnVisibility: true,
    // AG Grid reads it only when the grid is created, so it is set before any column is grouped.
    initialGroupOrderComparator: (params: InitialGroupOrderComparatorParams) =>
      compareRowGroupKeys(params.nodeA.key, params.nodeB.key),
  };
}

/** A group row selects the rows in it while grouping by a column. */
export function withRowGroupSelection(
  rowSelection: RowSelectionOptions | undefined,
  rowGroups: GridRowGroups | undefined,
): RowSelectionOptions | undefined {
  if (!isGroupingBy(rowGroups) || rowSelection?.mode !== "multiRow") return rowSelection;
  return { ...rowSelection, groupSelects: "descendants" };
}

/** AG Grid's "Group by" / "Un-group by" column menu items. */
export function isRowGroupMenuItem(item: DefaultMenuItem | MenuItemDef): item is "rowGroup" | "rowUnGroup" {
  return item === "rowGroup" || item === "rowUnGroup";
}

/** AG Grid's own row-grouping texts: the column menu items, the Group column and the empty panel. */
export function rowGroupLocaleText(t: (key: string) => string): Record<string, string> {
  return {
    groupBy: t("common.action.groupBy"),
    ungroupBy: t("common.action.ungroupBy"),
    ungroupAll: t("common.action.discardAllGroups"),
    group: t("common.label.rowGroupColumn"),
    rowGroupColumnsEmptyMessage: t("common.label.rowGroupsDropZone"),
  };
}

/** {@link ROW_GROUP_ICONS} as AG Grid icon markup. */
export function rowGroupIcons(markup: (icon: RawIconName) => string): Record<string, string> {
  return Object.fromEntries(Object.entries(ROW_GROUP_ICONS).map(([key, icon]) => [key, markup(icon)]));
}
